import React, { useState, useEffect } from 'react';
import { DollarSign, Save, RefreshCw, TrendingDown, AlertCircle, Globe, MapPin } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPricingManagement = ({ language }) => {
  const { token, isSuperAdmin } = useAdmin();
  const [selectedRegion, setSelectedRegion] = useState('pt');
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedValues, setEditedValues] = useState({});

  const regions = [
    { code: 'pt', name: language === 'pt' ? 'Portugal (Faro)' : 'Portugal (Faro)', flag: '🇵🇹' },
    { code: 'ie', name: language === 'pt' ? 'Irlanda (Dublin)' : 'Ireland (Dublin)', flag: '🇮🇪' }
  ];

  useEffect(() => {
    fetchPricing();
  }, [selectedRegion]);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/pricing?region=${selectedRegion}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data);
      
      // Initialize edited values
      const initial = {};
      response.data.forEach(p => {
        initial[p.service_key] = {
          market_average_price: p.market_average_price,
          discount_percentage: p.discount_percentage,
          minimum_price: p.minimum_price
        };
      });
      setEditedValues(initial);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (serviceKey, field, value) => {
    setEditedValues(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const savePricing = async (serviceKey) => {
    setSaving(prev => ({ ...prev, [serviceKey]: true }));
    
    try {
      await axios.patch(
        `${API}/admin/pricing/${serviceKey}?region=${selectedRegion}`,
        editedValues[serviceKey],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPricing();
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert(language === 'pt' ? 'Erro ao guardar preços' : 'Error saving prices');
    } finally {
      setSaving(prev => ({ ...prev, [serviceKey]: false }));
    }
  };

  const calculateOurPrice = (marketPrice, discount, minimum) => {
    const calculated = marketPrice * (1 - discount / 100);
    return Math.max(calculated, minimum);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (!isSuperAdmin()) {
    return (
      <div className="text-center py-12">
        <DollarSign size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">
          {language === 'pt' 
            ? 'Apenas Super Admins podem gerir preços' 
            : 'Only Super Admins can manage pricing'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
            <DollarSign size={24} />
            {language === 'pt' ? 'Gestão de Preços' : 'Pricing Management'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'pt' 
              ? 'Configure os preços dos serviços por região' 
              : 'Configure service prices by region'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={fetchPricing}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            {language === 'pt' ? 'Atualizar' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Region Selector */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <Globe size={20} className="text-primary" />
          <span className="font-medium text-gray-800">
            {language === 'pt' ? 'Selecionar Região' : 'Select Region'}
          </span>
        </div>
        <div className="flex flex-wrap gap-3">
          {regions.map((region) => (
            <button
              key={region.code}
              onClick={() => setSelectedRegion(region.code)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                selectedRegion === region.code
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-primary/50'
              }`}
            >
              <span className="text-lg">{region.flag}</span>
              <span>{region.name}</span>
              {selectedRegion === region.code && (
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {language === 'pt' ? 'Como funciona:' : 'How it works:'}
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                {language === 'pt' 
                  ? `Média de Mercado: Preço médio praticado em ${selectedRegion === 'pt' ? 'Faro' : 'Dublin'}` 
                  : `Market Average: Average price in ${selectedRegion === 'pt' ? 'Faro' : 'Dublin'}`}
              </li>
              <li>{language === 'pt' ? 'Desconto: Redução aplicada (5% a 15%)' : 'Discount: Applied reduction (5% to 15%)'}</li>
              <li>{language === 'pt' ? 'Preço Mínimo: Nunca calcularemos abaixo deste valor' : 'Minimum Price: We never calculate below this value'}</li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw size={32} className="mx-auto text-gray-400 animate-spin mb-4" />
          <p className="text-gray-500">{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
        </div>
      ) : (
        <>
          {/* Region Header */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
            <MapPin size={18} className="text-primary" />
            <span className="font-medium text-gray-700">
              {language === 'pt' ? 'Preços para' : 'Prices for'} {regions.find(r => r.code === selectedRegion)?.name}
            </span>
            <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">
              {pricing.length} {language === 'pt' ? 'serviços' : 'services'}
            </span>
          </div>

          {/* Pricing Cards */}
          <div className="space-y-4">
            {pricing.map((service) => {
              const edited = editedValues[service.service_key] || {};
              const ourPrice = calculateOurPrice(
                edited.market_average_price || service.market_average_price,
                edited.discount_percentage || service.discount_percentage,
                edited.minimum_price || service.minimum_price
              );
              const hasChanges = 
                edited.market_average_price !== service.market_average_price ||
                edited.discount_percentage !== service.discount_percentage ||
                edited.minimum_price !== service.minimum_price;

              return (
                <div 
                  key={service.service_key}
                  className={`border rounded-xl p-5 bg-white shadow-sm ${hasChanges ? 'border-yellow-400 ring-2 ring-yellow-100' : 'border-gray-200'}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-heading font-semibold text-lg">
                        {language === 'pt' ? service.service_name_pt : service.service_name_en}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {language === 'pt' ? `Cálculo por ${service.unit_label_pt}` : `Calculated per ${service.unit_label_en}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{language === 'pt' ? 'Preço Final' : 'Final Price'}</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(ourPrice)} <span className="text-sm font-normal text-gray-500">/ {service.unit_label_pt}</span>
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Market Average */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {language === 'pt' ? 'Média de Mercado (€)' : 'Market Average (€)'}
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        value={edited.market_average_price ?? service.market_average_price}
                        onChange={(e) => handleValueChange(service.service_key, 'market_average_price', e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    {/* Discount Percentage */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {language === 'pt' ? 'Desconto (5-15%)' : 'Discount (5-15%)'}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.5"
                          min="5"
                          max="15"
                          value={edited.discount_percentage ?? service.discount_percentage}
                          onChange={(e) => handleValueChange(service.service_key, 'discount_percentage', e.target.value)}
                          className="w-full h-10 px-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                      </div>
                    </div>

                    {/* Minimum Price */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {language === 'pt' ? 'Preço Mínimo (€)' : 'Minimum Price (€)'}
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        value={edited.minimum_price ?? service.minimum_price}
                        onChange={(e) => handleValueChange(service.service_key, 'minimum_price', e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Preview and Save */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500">
                        {language === 'pt' ? 'Mercado:' : 'Market:'} 
                        <span className="font-medium text-gray-700 ml-1">{formatCurrency(edited.market_average_price || service.market_average_price)}</span>
                      </span>
                      <TrendingDown size={16} className="text-green-500" />
                      <span className="text-green-600">
                        -{edited.discount_percentage || service.discount_percentage}%
                      </span>
                      <span className="text-gray-500">=</span>
                      <span className="font-bold text-primary">{formatCurrency(ourPrice)}</span>
                    </div>
                    
                    <button
                      onClick={() => savePricing(service.service_key)}
                      disabled={!hasChanges || saving[service.service_key]}
                      className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {saving[service.service_key] ? (
                        <RefreshCw size={16} className="animate-spin" />
                      ) : (
                        <Save size={16} />
                      )}
                      {language === 'pt' ? 'Guardar' : 'Save'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {pricing.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <DollarSign size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {language === 'pt' 
                  ? 'Nenhum serviço disponível para esta região' 
                  : 'No services available for this region'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPricingManagement;
